import React from 'react';
import { Button, InputNumber } from 'antd';
import {
  CheckCircleOutlined,
  CloseOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import SettingsCard from '../../SessionSettingsShared/components/SettingsCard';
import { BasketRow, LibOption, validateBasket, weightTotal } from './helpers';

interface Props {
  basket: BasketRow[];
  optionIndex: Record<string, LibOption>;
  onWeight: (ric: string, weight: number | null) => void;
  onRemove: (ric: string) => void;
  onEvenSplit: () => void;
  onSave: () => void;
  saving: boolean;
  dirty: boolean;
}

/**
 * Compact benchmark-basket tile. Sessions usually carry 1–5 constituents, so
 * this sizes to content (small) rather than filling the workspace — each row
 * keeps the name and weight close together instead of pushing the weight to a
 * far edge. The return chart lives in its own permanent tile beside it.
 */
const CompositionBuilderV2: React.FC<Props> = ({
  basket,
  optionIndex,
  onWeight,
  onRemove,
  onEvenSplit,
  onSave,
  saving,
  dirty,
}) => {
  const total = weightTotal(basket);
  const { valid, reason } = validateBasket(basket);

  const totalBadge = basket.length ? (
    <span className={`bmv2-total ${valid ? 'is-ok' : 'is-off'}`}>
      {total.toFixed(total % 1 === 0 ? 0 : 1)}%
    </span>
  ) : null;

  const footer = (
    <div className="bmv2-foot">
      <div className={`bmv2-foot__msg${valid ? '' : ' is-warn'}`}>
        {basket.length === 0 ? (
          <span>Empty basket</span>
        ) : valid ? (
          <>
            <CheckCircleOutlined style={{ color: 'var(--s2-positive)' }} />
            {dirty ? 'Ready to save' : 'Saved'}
          </>
        ) : (
          <>
            <WarningOutlined />
            {reason}
          </>
        )}
      </div>
      <div className="bmv2-foot__actions">
        <Button
          type="text"
          size="small"
          disabled={basket.length < 2}
          onClick={onEvenSplit}
        >
          Even split
        </Button>
        <Button
          type="primary"
          size="small"
          loading={saving}
          disabled={!valid || !dirty}
          onClick={onSave}
        >
          Save
        </Button>
      </div>
    </div>
  );

  return (
    <SettingsCard
      title="Benchmark basket"
      className="bmv2-card"
      pad0
      footer={footer}
      extra={totalBadge}
    >
      {basket.length === 0 ? (
        <div className="bmv2-empty bmv2-empty--tile">
          <span className="bmv2-empty__icon">◎</span>
          <span className="bmv2-empty__title">No constituents</span>
          <span className="bmv2-empty__sub">
            Add funds or equities from the library →
          </span>
        </div>
      ) : (
        <div className="bmv2-blist">
          {basket.map(row => {
            const meta = optionIndex[row.ric];
            const kind = meta?.kind ?? 'index_fund';
            return (
              <div className="bmv2-brow" key={row.ric}>
                <div className="bmv2-brow-main">
                  <span className="bmv2-brow-title">
                    {meta?.name ?? row.ric}
                  </span>
                  <span className="bmv2-brow-meta">
                    <span className="bmv2-ric">{row.ric}</span>
                    <span
                      className={`bmv2-tag bmv2-tag--${
                        kind === 'equity' ? 'equity' : 'fund'
                      }`}
                    >
                      {kind === 'equity' ? 'Equity' : 'Fund'}
                    </span>
                    {meta?.assetClass ? (
                      <span className="bmv2-tag">{meta.assetClass}</span>
                    ) : null}
                    {meta?.geography ? (
                      <span className="bmv2-tag">{meta.geography}</span>
                    ) : null}
                  </span>
                </div>
                <InputNumber
                  className="bmv2-brow-weight"
                  size="small"
                  min={0}
                  max={100}
                  step={10}
                  value={row.weight}
                  controls
                  formatter={v => `${v}%`}
                  parser={v => Number((v ?? '').replace('%', '')) || 0}
                  onChange={v => onWeight(row.ric, v as number | null)}
                />
                <button
                  type="button"
                  className="bmv2-x-btn"
                  aria-label="Remove"
                  onClick={() => onRemove(row.ric)}
                >
                  <CloseOutlined />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </SettingsCard>
  );
};

export default CompositionBuilderV2;
